/**
 * Scoped CSS for the chat widget.
 *
 * Everything is namespaced under `.nexor-chat` so it cannot leak into the host
 * page, and properties that hosts commonly override (color, font, line-height)
 * are set explicitly here so the host's reset can't bleed in.
 *
 * Animations are GPU-friendly (transform + opacity only — no layout-trashing
 * properties like top/left) and use modest durations so the widget feels
 * responsive on low-end devices.
 */
export const widgetCss = (accent: string, accentText: string): string => `
.nexor-chat,
.nexor-chat * {
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.nexor-chat {
  position: fixed;
  z-index: 2147483640;
  bottom: 20px;
  right: 20px;
  font-size: 14px;
  line-height: 1.4;
  color: #111;
}

.nexor-chat[data-position="bottom-left"] {
  right: auto;
  left: 20px;
}

/* ── Launcher wrapper (anchors the optional speed-dial fan) ───────── */
.nexor-chat__launcher-wrap {
  position: relative;
  display: inline-flex;
}

/* ── Launcher bubble ─────────────────────────────────────────────── */
.nexor-chat__launcher {
  width: 56px;
  height: 56px;
  border-radius: 9999px;
  background: ${accent};
  color: ${accentText};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  transition: transform 160ms cubic-bezier(.2,.7,.2,1.1),
              box-shadow 160ms ease,
              background 200ms ease;
  padding: 0;
  position: relative;
  animation: nexor-launcher-in 360ms cubic-bezier(.2,.7,.2,1.1) both;
}
.nexor-chat__launcher:hover {
  transform: translateY(-2px) scale(1.04);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.24);
}
.nexor-chat__launcher:active {
  transform: translateY(0) scale(0.97);
  transition-duration: 80ms;
}
.nexor-chat__launcher:focus-visible {
  outline: 2px solid ${accent};
  outline-offset: 2px;
}
.nexor-chat[data-open="true"] .nexor-chat__launcher {
  transform: scale(0.85);
  opacity: 0.75;
}
/* While the chat panel is open the launcher is dormant: no hover fan, no
   clicks. The visitor closes the chat (header ✕) to reach the channels again.
   pointer-events:none also stops the CSS :hover that would reveal the fan. */
.nexor-chat[data-open="true"] .nexor-chat__launcher-wrap {
  pointer-events: none;
}
.nexor-chat__launcher svg { width: 24px; height: 24px; transition: transform 200ms ease; }
.nexor-chat[data-open="true"] .nexor-chat__launcher svg { transform: rotate(8deg); }

.nexor-chat__unread {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  background: #ef4444;
  color: #fff;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  padding: 0 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
  animation: nexor-pop-in 300ms cubic-bezier(.2,.7,.2,1.1) both;
}

@keyframes nexor-launcher-in {
  from { transform: translateY(20px) scale(0.6); opacity: 0; }
  to   { transform: translateY(0) scale(1);     opacity: 1; }
}
@keyframes nexor-pop-in {
  from { transform: scale(0); }
  to   { transform: scale(1); }
}

/* ── Speed-dial channel fan ───────────────────────────────────────── */
/* Sits above the launcher, out of normal flow so it never shifts the bubble.
   Hidden until the wrapper is hovered (pointer devices) or tapped open
   (data-expanded, set by JS — the only path on touch). */
.nexor-chat__channels {
  position: absolute;
  /* Anchor the fan's box to the top of the launcher and push the buttons up
     with padding instead of a gap. This keeps the launcher + fan a single,
     continuous hover surface — moving the cursor from the bubble up to the
     buttons never crosses dead space, so the drawer no longer snaps shut. */
  bottom: 100%;
  right: 0;
  padding-bottom: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-end;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: translateY(10px);
  transition: opacity 180ms ease, transform 180ms ease, visibility 180ms;
}
.nexor-chat[data-position="bottom-left"] .nexor-chat__channels {
  right: auto;
  left: 0;
  align-items: flex-start;
}

@media (hover: hover) and (pointer: fine) {
  .nexor-chat__launcher-wrap:hover .nexor-chat__channels {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transform: translateY(0);
  }
}
.nexor-chat__launcher-wrap[data-expanded="true"] .nexor-chat__channels {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  transform: translateY(0);
}

/* Subtle rise-and-stagger as each item appears (nearest the launcher first). */
.nexor-chat__channel-item {
  display: flex;
  align-items: center;
  gap: 10px;
  transform: translateY(8px);
  opacity: 0;
  transition: transform 200ms cubic-bezier(.2,.7,.2,1.1), opacity 200ms ease;
}
.nexor-chat[data-position="bottom-left"] .nexor-chat__channel-item {
  flex-direction: row-reverse;
}
.nexor-chat__launcher-wrap[data-expanded="true"] .nexor-chat__channel-item,
.nexor-chat__launcher-wrap:hover .nexor-chat__channel-item {
  transform: translateY(0);
  opacity: 1;
}
.nexor-chat__channel-item:nth-last-child(1) { transition-delay: 0ms; }
.nexor-chat__channel-item:nth-last-child(2) { transition-delay: 40ms; }
.nexor-chat__channel-item:nth-last-child(3) { transition-delay: 80ms; }
.nexor-chat__channel-item:nth-last-child(4) { transition-delay: 120ms; }
.nexor-chat__channel-item:nth-last-child(5) { transition-delay: 160ms; }

/* The round mini-button for each channel. */
.nexor-chat__channel {
  width: 46px;
  height: 46px;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: ${accent};
  color: ${accentText};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  text-decoration: none;
  transition: transform 140ms cubic-bezier(.2,.7,.2,1.1), box-shadow 140ms ease, filter 140ms ease;
}
.nexor-chat__channel:hover {
  transform: scale(1.08);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.26);
  filter: brightness(1.05);
}
.nexor-chat__channel:active { transform: scale(0.94); }
.nexor-chat__channel:focus-visible { outline: 2px solid ${accent}; outline-offset: 2px; }
.nexor-chat__channel svg { width: 24px; height: 24px; display: block; }

/* Brand colours win over the accent where a channel has a recognisable hue. */
.nexor-chat__channel--whatsapp { background: #25d366; color: #fff; }
.nexor-chat__channel--instagram {
  background: radial-gradient(circle at 30% 110%, #fdf497 0%, #fd5949 45%, #d6249f 70%, #285aeb 100%);
  color: #fff;
}

/* Label pill beside each button. */
.nexor-chat__channel-label {
  background: #fff;
  color: #111;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 10px;
  border-radius: 8px;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.16);
}

/* ── Panel ────────────────────────────────────────────────────────── */
.nexor-chat__panel {
  position: absolute;
  bottom: 72px;
  right: 0;
  width: 360px;
  max-width: calc(100vw - 32px);
  height: 540px;
  max-height: calc(100vh - 120px);
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.22);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform: translateY(14px) scale(0.96);
  opacity: 0;
  pointer-events: none;
  transition: transform 220ms cubic-bezier(.2,.7,.2,1.1),
              opacity 220ms ease;
  border: 1px solid rgba(0, 0, 0, 0.06);
  /* Use visibility so screen readers don't read collapsed content */
  visibility: hidden;
}
.nexor-chat[data-position="bottom-left"] .nexor-chat__panel {
  right: auto;
  left: 0;
}
.nexor-chat[data-open="true"] .nexor-chat__panel {
  transform: translateY(0) scale(1);
  opacity: 1;
  pointer-events: auto;
  visibility: visible;
}

.nexor-chat__header {
  background: ${accent};
  color: ${accentText};
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.nexor-chat__title {
  font-weight: 600;
  font-size: 15px;
  margin: 0;
}
.nexor-chat__subtitle {
  font-size: 12px;
  opacity: 0.85;
  margin: 2px 0 0;
  line-height: 1.4;
}
.nexor-chat__status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: #34d399;
  margin-right: 6px;
  vertical-align: 1px;
  animation: nexor-pulse 2.4s ease-in-out infinite;
  box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.6);
}
@keyframes nexor-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.55); }
  50%      { box-shadow: 0 0 0 6px rgba(52, 211, 153, 0);    }
}
.nexor-chat__close {
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  opacity: 0.9;
  transition: background 140ms ease, transform 140ms ease;
}
.nexor-chat__close:hover { background: rgba(255,255,255,0.18); opacity: 1; }
.nexor-chat__close:active { transform: scale(0.92); }
.nexor-chat__close svg { width: 18px; height: 18px; display: block; }
/* Keep the title left and group the action buttons on the right. */
.nexor-chat__header > div { margin-right: auto; min-width: 0; }
.nexor-chat__restart {
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  opacity: 0.7;
  transition: background 140ms ease, transform 200ms ease, opacity 140ms ease;
}
.nexor-chat__restart:hover { background: rgba(255,255,255,0.18); opacity: 1; }
.nexor-chat__restart:active { transform: rotate(-90deg) scale(0.9); }
.nexor-chat__restart svg { width: 17px; height: 17px; display: block; }
/* Nothing to reset on the capture screen — hide it there. */
.nexor-chat__panel[data-view="capture"] .nexor-chat__restart { display: none; }

/* ── Body / messages ──────────────────────────────────────────────── */
.nexor-chat__body {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  background: #f6f7f9;
  display: flex;
  flex-direction: column;
  gap: 8px;
  scroll-behavior: smooth;
  /* Prevent layout shift when scrollbar appears */
  scrollbar-gutter: stable;
}
.nexor-chat__body::-webkit-scrollbar { width: 6px; }
.nexor-chat__body::-webkit-scrollbar-thumb {
  background: rgba(0,0,0,0.15);
  border-radius: 9999px;
}

.nexor-chat__msg {
  max-width: 80%;
  padding: 8px 12px;
  border-radius: 14px;
  white-space: pre-wrap;
  word-wrap: break-word;
  animation: nexor-msg-in 240ms cubic-bezier(.2,.7,.2,1.1) both;
  will-change: transform, opacity;
}
.nexor-chat__msg--bot {
  background: #fff;
  align-self: flex-start;
  border: 1px solid rgba(0,0,0,0.06);
  border-bottom-left-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
}
.nexor-chat__msg--user {
  background: ${accent};
  color: ${accentText};
  align-self: flex-end;
  border-bottom-right-radius: 4px;
  animation-name: nexor-msg-in-user;
}
.nexor-chat__msg--system {
  background: transparent;
  color: #6b7280;
  font-size: 12px;
  align-self: center;
  text-align: center;
  max-width: 100%;
  animation-name: nexor-fade-in;
}
.nexor-chat__msg-time {
  display: block;
  margin-top: 4px;
  font-size: 10px;
  line-height: 1;
  opacity: 0.55;
  text-align: right;
}
.nexor-chat__msg--bot .nexor-chat__msg-time { text-align: left; }

@keyframes nexor-msg-in {
  from { transform: translateY(8px) scale(0.96); opacity: 0; }
  to   { transform: translateY(0) scale(1);     opacity: 1; }
}
@keyframes nexor-msg-in-user {
  from { transform: translateY(8px) translateX(8px) scale(0.96); opacity: 0; }
  to   { transform: translateY(0) translateX(0) scale(1);        opacity: 1; }
}
@keyframes nexor-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.nexor-chat__typing {
  align-self: flex-start;
  background: #fff;
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 14px;
  border-bottom-left-radius: 4px;
  padding: 10px 14px;
  display: inline-flex;
  gap: 4px;
  animation: nexor-msg-in 240ms cubic-bezier(.2,.7,.2,1.1) both;
}
.nexor-chat__typing span {
  width: 6px; height: 6px; border-radius: 9999px;
  background: #9ca3af;
  display: inline-block;
  animation: nexor-blink 1.2s infinite ease-in-out;
}
.nexor-chat__typing span:nth-child(2) { animation-delay: 0.15s; }
.nexor-chat__typing span:nth-child(3) { animation-delay: 0.30s; }
@keyframes nexor-blink {
  0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
  30%           { opacity: 1;   transform: translateY(-3px); }
}

/* ── Resume banner (returning visitor) ────────────────────────────── */
.nexor-chat__resume {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: #fff;
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 10px;
  padding: 8px 12px;
  margin-bottom: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
  animation: nexor-fade-in 240ms ease both;
}
.nexor-chat__resume-text {
  font-size: 12px;
  color: #6b7280;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nexor-chat__resume-reset {
  flex: none;
  background: transparent;
  border: none;
  color: ${accent};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 6px;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: opacity 140ms ease;
}
.nexor-chat__resume-reset:hover { opacity: 0.7; }
.nexor-chat__resume-reset:active { opacity: 0.5; }

/* ── Capture as its own full-panel view ───────────────────────────── */
/* The form is a distinct screen, not a card wedged into the message list.
   data-view swaps between the capture screen and the chat (body + composer). */
.nexor-chat__capture-view {
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  background: #f6f7f9;
  display: flex;
  flex-direction: column;
  animation: nexor-fade-in 200ms ease both;
}
/* In the form view the panel hugs the form — no fixed 540px height leaving
   empty gaps above and below the card. It still can't exceed max-height. */
.nexor-chat__panel[data-view="capture"] {
  height: auto;
}
.nexor-chat__panel[data-view="chat"] .nexor-chat__capture-view { display: none; }
.nexor-chat__panel[data-view="capture"] .nexor-chat__body,
.nexor-chat__panel[data-view="capture"] .nexor-chat__composer { display: none; }
.nexor-chat__capture-view .nexor-chat__capture-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

/* ── Lead capture form ────────────────────────────────────────────── */
.nexor-chat__capture {
  background: #fff;
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
  animation: nexor-msg-in 280ms cubic-bezier(.2,.7,.2,1.1) both;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}
.nexor-chat__capture-label {
  font-size: 12px;
  color: #6b7280;
  margin: 0;
}
.nexor-chat__capture-row {
  display: flex;
  gap: 8px;
}
.nexor-chat__capture-row > * { flex: 1; }
.nexor-chat__input,
.nexor-chat__capture input {
  font: inherit;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #111;
  outline: none;
  width: 100%;
  transition: border-color 140ms ease, box-shadow 140ms ease;
}
/* Composer textarea: auto-grows (height managed in JS) up to ~4 lines, then
   scrolls. resize:none disables the manual drag handle. */
textarea.nexor-chat__input {
  display: block;
  resize: none;
  overflow-y: auto;
  line-height: 20px;
  max-height: 98px; /* 4 lines (20×4) + 16 padding + 2 border */
  white-space: pre-wrap;
  word-break: break-word;
}
.nexor-chat__input:focus,
.nexor-chat__capture input:focus {
  border-color: ${accent};
  box-shadow: 0 0 0 3px ${accent}33;
}
.nexor-chat__input--error,
.nexor-chat__capture input.nexor-chat__input--error {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.16);
}
.nexor-chat__capture-error {
  font-size: 12px;
  color: #ef4444;
  margin: 0;
}
.nexor-chat__capture-error[hidden] { display: none; }

/* ── Phone field (flag + dial code selector joined to a digit input) ── */
.nexor-chat__phone {
  display: flex;
  align-items: stretch;
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
  transition: border-color 140ms ease, box-shadow 140ms ease;
}
.nexor-chat__phone:focus-within {
  border-color: ${accent};
  box-shadow: 0 0 0 3px ${accent}33;
}
.nexor-chat__phone.nexor-chat__input--error {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.16);
}
.nexor-chat__phone-country {
  flex: none;
  border: none;
  border-right: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #111;
  font: inherit;
  padding: 0 12px 0 10px;
  cursor: pointer;
  outline: none;
}
.nexor-chat__phone-number.nexor-chat__input {
  border: none;
  border-radius: 0;
  box-shadow: none;
  flex: 1;
  min-width: 0;
}

.nexor-chat__capture-submit {
  background: ${accent};
  color: ${accentText};
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: transform 140ms ease, filter 140ms ease;
}
.nexor-chat__capture-submit:hover { filter: brightness(1.08); }
.nexor-chat__capture-submit:active { transform: scale(0.97); }
.nexor-chat__capture-submit[disabled] { opacity: 0.6; cursor: not-allowed; }

/* ── Contact-request view (call me / email me) ────────────────────── */
/* Its own full-panel screen, opened from a "call"/"email" fan button. Reuses
   the capture form styling (.nexor-chat__capture) for the fields. */
.nexor-chat__contact-view {
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  background: #f6f7f9;
  display: flex;
  flex-direction: column;
  animation: nexor-fade-in 200ms ease both;
}
/* Show the contact view only in its own data-view; hug the form (no fixed
   height leaving gaps), but never exceed the panel's max-height. */
.nexor-chat__panel[data-view="contact"] { height: auto; }
.nexor-chat__panel:not([data-view="contact"]) .nexor-chat__contact-view { display: none; }
.nexor-chat__panel[data-view="contact"] .nexor-chat__capture-view,
.nexor-chat__panel[data-view="contact"] .nexor-chat__body,
.nexor-chat__panel[data-view="contact"] .nexor-chat__composer { display: none; }
.nexor-chat__panel[data-view="contact"] .nexor-chat__restart { display: none; }

/* The contact form lives under the header, which carries its title/explainer;
   the live-status dot ("we're listening") makes no sense for a form. */
.nexor-chat__panel[data-view="contact"] .nexor-chat__status-dot { display: none; }

/* form ↔ success swap, driven by the view's data-state */
.nexor-chat__contact-view[data-state="sent"] .nexor-chat__contact-form { display: none; }
.nexor-chat__contact-view:not([data-state="sent"]) .nexor-chat__contact-success { display: none; }

.nexor-chat__contact-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  padding: 28px 12px 12px;
  animation: nexor-msg-in 280ms cubic-bezier(.2,.7,.2,1.1) both;
}
.nexor-chat__contact-success-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${accent};
  color: ${accentText};
}
.nexor-chat__contact-success-icon svg { width: 26px; height: 26px; }
.nexor-chat__contact-success-text {
  font-size: 14px;
  color: #374151;
  margin: 0;
  line-height: 1.5;
}
.nexor-chat__contact-success .nexor-chat__capture-submit {
  margin-top: 4px;
  align-self: stretch;
}

/* ── Composer ─────────────────────────────────────────────────────── */
.nexor-chat__composer {
  display: flex;
  gap: 8px;
  padding: 10px;
  background: #fff;
  border-top: 1px solid rgba(0,0,0,0.06);
  /* Pin the send button to the bottom so it stays put as the textarea grows. */
  align-items: flex-end;
}
.nexor-chat__send {
  background: ${accent};
  color: ${accentText};
  border: none;
  border-radius: 8px;
  padding: 0 14px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  min-width: 44px;
  height: 38px; /* matches the one-line textarea height */
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 140ms ease, filter 140ms ease;
}
.nexor-chat__send:hover { filter: brightness(1.08); }
.nexor-chat__send:active { transform: scale(0.95); }
.nexor-chat__send[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}
.nexor-chat__send svg {
  width: 18px;
  height: 18px;
  transition: transform 200ms ease;
}
.nexor-chat__send:not([disabled]):hover svg { transform: translateX(2px); }

/* Small spinner inside the send button while a turn is in flight */
.nexor-chat__send--loading svg { display: none; }
.nexor-chat__send--loading::after {
  content: "";
  width: 14px;
  height: 14px;
  border-radius: 9999px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  animation: nexor-spin 700ms linear infinite;
}
@keyframes nexor-spin {
  to { transform: rotate(360deg); }
}

.nexor-chat__footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 11px;
  color: #9ca3af;
  padding: 6px;
  background: #fff;
  border-top: 1px solid rgba(0,0,0,0.04);
}
.nexor-chat__footer a {
  color: inherit;
  text-decoration: none;
  font-weight: 600;
}
/* "Powered by" + the Nexor wordmark (same mark as the site navbar). Plain
   attribution — not a link, but hovering shows a getnexor.ai tooltip. */
.nexor-chat__brand {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: help; /* hover reveals the getnexor.ai tooltip; not a link */
}
.nexor-chat__brand-logo {
  height: 13px;
  width: auto;
  display: block;
}
/* Custom tooltip (instant + reliable across browsers, unlike a native SVG
   <title>). Sits above the logo and fades in on hover/focus. */
.nexor-chat__brand-tip {
  position: absolute;
  bottom: calc(100% + 7px);
  left: 50%;
  transform: translateX(-50%) translateY(4px);
  background: #111827;
  color: #fff;
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0.01em;
  padding: 5px 8px;
  border-radius: 6px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0,0,0,0.18);
  transition: opacity 140ms ease, transform 140ms ease;
  z-index: 5;
}
.nexor-chat__brand-tip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 4px solid transparent;
  border-top-color: #111827;
}
.nexor-chat__brand:hover .nexor-chat__brand-tip {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

/* ── Mobile: full-screen panel with an obvious close affordance ───── */
@media (max-width: 480px) {
  /* The panel takes over the whole viewport so the keyboard + messages have
     room. 100dvh tracks the dynamic viewport as mobile browser chrome shows/
     hides, avoiding a cut-off composer. */
  .nexor-chat__panel {
    position: fixed;
    inset: 0;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    max-width: 100%;
    height: 100vh;
    height: 100dvh;
    max-height: none;
    border-radius: 0;
    border: none;
    transform: translateY(100%);
  }
  .nexor-chat[data-position="bottom-left"] .nexor-chat__panel { left: 0; }
  .nexor-chat[data-open="true"] .nexor-chat__panel { transform: translateY(0); }
  /* Keep the form view full-screen on phones too (overrides the desktop
     hug-the-form height). */
  .nexor-chat__panel[data-view="capture"] {
    height: 100vh;
    height: 100dvh;
  }
  .nexor-chat__panel[data-view="capture"] .nexor-chat__capture-view {
    justify-content: center;
  }

  /* Hide the launcher (and any open fan) while the full-screen panel is up —
     the in-header close button is the way back out. */
  .nexor-chat[data-open="true"] .nexor-chat__launcher-wrap {
    opacity: 0;
    pointer-events: none;
  }

  /* Roomier header + a bigger, unmistakable close target on touch. */
  .nexor-chat__header {
    padding: 16px 14px 16px 18px;
    padding-top: max(16px, env(safe-area-inset-top));
  }
  .nexor-chat__close {
    padding: 10px;
    background: rgba(255, 255, 255, 0.16);
  }
  .nexor-chat__close svg { width: 24px; height: 24px; }

  /* Keep the composer clear of the home-indicator / gesture bar. */
  .nexor-chat__composer {
    padding-bottom: max(10px, env(safe-area-inset-bottom));
  }
}

/* Respect users who request reduced motion. */
@media (prefers-reduced-motion: reduce) {
  .nexor-chat,
  .nexor-chat * {
    animation-duration: 0.001ms !important;
    transition-duration: 0.001ms !important;
  }
  .nexor-chat__status-dot { animation: none; }
}
`;
